import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../auth/AuthProvider';
import { hasPermission, PERMISSIONS } from '../../auth/permissions';
import { invalidateQueries } from '../../hooks/useCachedQuery';
import { colors, radius, shadows } from '../../styles/theme';
import { logError } from '../../utils/logError';
import SearchableSelect from '../../components/SearchableSelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return { 'Authorization': `Bearer ${token}`, ...extra };
};

const DURUM_COLORS = {
  TASLAK: { bg: '#e3f2fd', color: '#1565c0' },
  GONDERILDI: { bg: '#fff3e0', color: '#e65100' },
  TAMAMLANDI: { bg: '#e8f5e9', color: '#2e7d32' },
  BEKLIYOR: { bg: '#fff3e0', color: '#e65100' },
  ODENDI: { bg: '#e8f5e9', color: '#2e7d32' }
};

const OdemePlaniPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [planlar, setPlanlar] = useState([]);
  const [tedarikciler, setTedarikçiler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list / create / detail
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [filter, setFilter] = useState('ALL');

  // Form state
  const [formKalemler, setFormKalemler] = useState([]);
  const [formNotlar, setFormNotlar] = useState('');
  const [newTedarikciId, setNewTedarikciId] = useState('');
  const [newTutar, setNewTutar] = useState('');
  const [newNot, setNewNot] = useState('');

  // Ödeme modal
  const [payModal, setPayModal] = useState(null); // { kalemId, tedarikçiUnvan, tutar }
  const [payMethod, setPayMethod] = useState('HAVALE');

  const canCreate = hasPermission(user.role, PERMISSIONS.CREATE_PAYMENT_PLAN, user.effectivePermissions);
  const canManage = hasPermission(user.role, PERMISSIONS.MANAGE_PAYMENT_PLAN, user.effectivePermissions);

  const loadPlanlar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari`, { headers: getAuthHeaders() });
      if (res.ok) setPlanlar(await res.json());
    } catch (err) { logError('Plan yükleme hatası:', err); }
    finally { setLoading(false); }
  }, []);

  const loadTedarikçiler = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/tedarikciler`, { headers: getAuthHeaders() });
      if (res.ok) setTedarikçiler(await res.json());
    } catch (err) { logError('Tedarikçi yükleme hatası:', err); }
  }, []);

  useEffect(() => { loadPlanlar(); loadTedarikçiler(); }, [loadPlanlar, loadTedarikçiler]);

  const tedarikçiOptions = useMemo(() =>
    tedarikciler.map(t => ({ value: t.id, label: t.unvan || t.kod })), [tedarikciler]
  );

  const filteredPlanlar = useMemo(() => {
    if (filter === 'ALL') return planlar;
    return planlar.filter(p => p.durum === filter);
  }, [planlar, filter]);

  // ============ PLAN OLUŞTURMA ============
  const handleAddKalem = () => {
    if (!newTedarikciId || !newTutar || parseFloat(newTutar) <= 0) return;
    const ted = tedarikciler.find(t => t.id === parseInt(newTedarikciId));
    setFormKalemler([...formKalemler, {
      tedarikci_id: parseInt(newTedarikciId),
      tedarikci_unvan: ted ? (ted.unvan || ted.kod) : '?',
      tutar: parseFloat(newTutar),
      not_kalem: newNot || null
    }]);
    setNewTedarikciId('');
    setNewTutar('');
    setNewNot('');
  };

  const handleRemoveKalem = (idx) => {
    setFormKalemler(formKalemler.filter((_, i) => i !== idx));
  };

  const handleSaveDraft = async () => {
    if (formKalemler.length === 0) { toast.warning('En az bir kalem ekleyin'); return; }
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ kalemler: formKalemler.map(k => ({ tedarikci_id: k.tedarikci_id, tutar: k.tutar, not_kalem: k.not_kalem })), notlar: formNotlar || null })
      });
      if (res.ok) {
        const data = await res.json();
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl']);
        toast.success(`${data.plan_no} oluşturuldu`);
        setView('list');
        setFormKalemler([]);
        setFormNotlar('');
        loadPlanlar();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Hata oluştu');
      }
    } catch (err) { toast.error('Baglanti hatası'); }
  };

  const handleSendPlan = async (planId) => {
    if (!window.confirm('Planı gondermek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari/${planId}/gonder`, {
        method: 'PATCH', headers: getAuthHeaders()
      });
      if (res.ok) {
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl']);
        toast.success('Plan gönderildi');
        loadPlanlar();
        if (selectedPlan && selectedPlan.id === planId) loadPlanDetail(planId);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Hata');
      }
    } catch (err) { toast.error('Baglanti hatası'); }
  };

  const handleDeletePlan = async (planId, planNo) => {
    if (!window.confirm(`${planNo} silinsin mi?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari/${planId}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (res.ok) {
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl']);
        loadPlanlar();
        if (view === 'detail') setView('list');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Hata');
      }
    } catch (err) { toast.error('Baglanti hatası'); }
  };

  // ============ PLAN DETAY ============
  const loadPlanDetail = async (planId) => {
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari/${planId}`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSelectedPlan(data);
        setView('detail');
      }
    } catch (err) { logError('Plan detay hatası:', err); }
  };

  // ============ KALEM ÖDE ============
  const handlePayKalem = async () => {
    if (!payModal) return;
    try {
      const res = await fetch(`${API_URL}/api/odeme-planlari/${selectedPlan.id}/kalemler/${payModal.kalemId}/ode`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ odeme_yontemi: payMethod })
      });
      if (res.ok) {
        const data = await res.json();
        invalidateQueries(['finance', 'dashboard', 'gider', 'pnl']);
        setPayModal(null);
        loadPlanDetail(selectedPlan.id);
        loadPlanlar();
        if (data.plan_tamamlandı) toast.success('Tüm kalemler ödendi — plan tamamlandı!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Hata');
      }
    } catch (err) { toast.error('Baglanti hatası'); }
  };

  // ============ TEDARİKÇİ BAKİYE ============
  const [tedarikciBakiye, setTedarikciBakiye] = useState(null);
  const [bakiyeLoading, setBakiyeLoading] = useState(false);

  useEffect(() => {
    if (!newTedarikciId) { setTedarikciBakiye(null); return; }
    setBakiyeLoading(true);
    fetch(`${API_URL}/api/finance/suppliers/${newTedarikciId}/balance`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setTedarikciBakiye(d ? (d.guncel_bakiye || 0) : null); })
      .catch(() => setTedarikciBakiye(null))
      .finally(() => setBakiyeLoading(false));
  }, [newTedarikciId]);

  // ============ EXCEL EXPORT ============
  const handleExcelExport = () => {
    if (!selectedPlan || !selectedPlan.kalemler || selectedPlan.kalemler.length === 0) return;
    const csvHeaders = ['Tedarikci', 'Tutar', 'Durum', 'Odeme Yontemi', 'Odeme Tarihi', 'Not'];
    const csvRows = selectedPlan.kalemler.map(k => [
      k.tedarikci_unvan || '-',
      k.tutar || 0,
      k.durum || '-',
      k.odeme_yontemi || '-',
      k.odenen_tarih || '-',
      k.not_kalem || '-',
    ]);
    const csv = [csvHeaders.join(';'), ...csvRows.map(r => r.join(';'))].join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `odeme-plani-${selectedPlan.plan_no || 'export'}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formToplam = formKalemler.reduce((s, k) => s + k.tutar, 0);

  // ============ STYLES ============
  const s = {
    container: { padding: '24px', maxWidth: '1200px', margin: '0 auto' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    title: { fontSize: '24px', fontWeight: '700', color: '#1a1a2e' },
    btn: (bg, color) => ({ padding: '10px 20px', background: bg, color, border: 'none', borderRadius: radius.md, cursor: 'pointer', fontWeight: '600', fontSize: '14px' }),
    btnSm: (bg, color) => ({ padding: '6px 14px', background: bg, color, border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontWeight: '500', fontSize: '13px' }),
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
    kpiCard: (color) => ({ background: colors.surface, borderRadius: radius.lg, padding: '20px', borderLeft: `4px solid ${color}`, boxShadow: shadows.md }),
    kpiLabel: { fontSize: '13px', color: colors.textSecondary, marginBottom: '4px' },
    kpiValue: (color) => ({ fontSize: '28px', fontWeight: '700', color }),
    filterRow: { display: 'flex', gap: '8px', marginBottom: '20px' },
    filterBtn: (active) => ({ padding: '8px 16px', borderRadius: radius.md, border: 'none', cursor: 'pointer', fontWeight: active ? '600' : '400', fontSize: '13px', background: active ? '#1a1a2e' : '#f0f0f0', color: active ? '#fff' : '#555' }),
    card: { background: colors.surface, borderRadius: radius.lg, padding: '20px', boxShadow: shadows.md, marginBottom: '16px', cursor: 'pointer', border: '1px solid #f0f0f0' },
    badge: (durum) => {
      const c = DURUM_COLORS[durum] || { bg: '#f5f5f5', color: colors.textSecondary };
      return { display: 'inline-block', padding: '4px 10px', borderRadius: radius.xl, fontSize: '12px', fontWeight: '500', background: c.bg, color: c.color };
    },
    table: { width: '100%', borderCollapse: 'collapse', background: colors.surface, borderRadius: radius.lg, overflow: 'hidden', boxShadow: shadows.md },
    th: { padding: '12px 16px', textAlign: 'left', background: colors.backgroundAlt, color: colors.textSecondary, fontSize: '12px', fontWeight: '600', borderBottom: `2px solid ${colors.border}` },
    td: { padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontSize: '14px' },
    formGroup: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', color: '#555', marginBottom: '6px' },
    input: { width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box' },
    modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
    modalContent: { background: colors.surface, borderRadius: radius.lg, padding: '24px', width: '400px', maxWidth: '92vw', boxSizing: 'border-box' }
  };

  if (loading && planlar.length === 0) return <div style={s.container}><p>Yükleniyor...</p></div>;

  // ============ PLAN OLUŞTURMA GÖRÜNÜMÜ ============
  if (view === 'create') {
    return (
      <div style={s.container}>
        <div style={s.header}>
          <h1 style={s.title}>Yeni Ödeme Planı</h1>
          <button onClick={() => { setView('list'); setFormKalemler([]); setFormNotlar(''); }} style={s.btn('#f0f0f0', '#555')}>Geri</button>
        </div>

        <div style={{ background: '#f0f4ff', padding: '12px 16px', borderRadius: radius.md, marginBottom: '20px', fontSize: '14px', color: '#1565c0' }}>
          Hafta araligi otomatik belirlenir (Cuma - Cuma)
        </div>

        {/* Kalem ekleme */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.lg, boxShadow: shadows.md, marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Kalem Ekle</h3>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: 2 }}>
              <label style={s.label}>Tedarikçi</label>
              <SearchableSelect options={tedarikçiOptions} value={newTedarikciId} onChange={setNewTedarikciId} placeholder="Tedarikçi sec..." minWidth="220px" />
              {newTedarikciId && (
                <div style={{ fontSize: '12px', marginTop: '4px', color: tedarikciBakiye > 0 ? '#d32f2f' : tedarikciBakiye < 0 ? '#2e7d32' : colors.textSecondary }}>
                  {bakiyeLoading ? 'Bakiye yukleniyor...' : tedarikciBakiye != null ? `Bakiye: ${Math.abs(tedarikciBakiye).toLocaleString('tr-TR')} TL ${tedarikciBakiye > 0 ? '(Borclu)' : tedarikciBakiye < 0 ? '(Alacakli)' : '(Dengede)'}` : ''}
                </div>
              )}
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Tutar (TL)</label>
              <input type="number" value={newTutar} onChange={e => setNewTutar(e.target.value)} placeholder="0.00" style={s.input} min="0" step="0.01" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={s.label}>Not</label>
              <input type="text" value={newNot} onChange={e => setNewNot(e.target.value)} placeholder="Açıklama..." style={s.input} />
            </div>
            <button onClick={handleAddKalem} style={s.btn('#1a1a2e', '#fff')}>Ekle</button>
          </div>
        </div>

        {/* Kalem listesi */}
        {formKalemler.length > 0 && (
          <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.lg, boxShadow: shadows.md, marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '16px' }}>Kalemler ({formKalemler.length})</h3>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Tedarikçi</th>
                  <th style={s.th}>Tutar</th>
                  <th style={s.th}>Not</th>
                  <th style={s.th}></th>
                </tr>
              </thead>
              <tbody>
                {formKalemler.map((k, i) => (
                  <tr key={i}>
                    <td style={s.td}>{k.tedarikci_unvan}</td>
                    <td style={{ ...s.td, fontWeight: '600' }}>{k.tutar.toLocaleString('tr-TR')} TL</td>
                    <td style={s.td}>{k.not_kalem || '-'}</td>
                    <td style={s.td}><button onClick={() => handleRemoveKalem(i)} style={s.btnSm('#ffebee', '#d32f2f')}>Sil</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ textAlign: 'right', marginTop: '12px', fontSize: '18px', fontWeight: '700', color: '#1a1a2e' }}>
              Toplam: {formToplam.toLocaleString('tr-TR')} TL
            </div>
          </div>
        )}

        {/* Plan notu */}
        <div style={{ background: colors.surface, padding: '20px', borderRadius: radius.lg, boxShadow: shadows.md, marginBottom: '20px' }}>
          <label style={s.label}>Plan Notu</label>
          <textarea value={formNotlar} onChange={e => setFormNotlar(e.target.value)} placeholder="Genel not..." style={{ ...s.input, minHeight: '60px', resize: 'vertical' }} />
        </div>

        {/* Aksiyonlar */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button onClick={handleSaveDraft} style={s.btn('#e3f2fd', '#1565c0')} disabled={formKalemler.length === 0}>Taslak Kaydet</button>
        </div>
      </div>
    );
  }

  // ============ PLAN DETAY GÖRÜNÜMÜ ============
  if (view === 'detail' && selectedPlan) {
    const p = selectedPlan;
    const odenTutar = p.odenen_tutar || 0;
    const kalanTutar = p.kalan_tutar || 0;

    return (
      <div style={s.container}>
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{p.plan_no}</h1>
            <span style={{ fontSize: '13px', color: colors.textTertiary }}>{p.hafta_baslangic} — {p.hafta_bitis} | Oluşturan: {p.oluşturan}</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={handleExcelExport} style={s.btn('#e8f5e9', '#2e7d32')}>Excel Indir</button>
            {canCreate && p.durum === 'TASLAK' && (
              <>
                <button onClick={() => handleSendPlan(p.id)} style={s.btn('#1a1a2e', '#fff')}>Gonder</button>
                <button onClick={() => handleDeletePlan(p.id, p.plan_no)} style={s.btn('#ffebee', '#d32f2f')}>Sil</button>
              </>
            )}
            <button onClick={() => { setView('list'); setSelectedPlan(null); }} style={s.btn('#f0f0f0', '#555')}>Geri</button>
          </div>
        </div>

        {/* KPI */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={s.kpiCard('#1565c0')}>
            <div style={s.kpiLabel}>Durum</div>
            <span style={s.badge(p.durum)}>{p.durum}</span>
          </div>
          <div style={s.kpiCard('#1a1a2e')}>
            <div style={s.kpiLabel}>Toplam</div>
            <div style={s.kpiValue('#1a1a2e')}>{(p.toplam_tutar || 0).toLocaleString('tr-TR')} TL</div>
          </div>
          <div style={s.kpiCard('#2e7d32')}>
            <div style={s.kpiLabel}>Odenen</div>
            <div style={s.kpiValue('#2e7d32')}>{odenTutar.toLocaleString('tr-TR')} TL</div>
          </div>
          <div style={s.kpiCard('#e65100')}>
            <div style={s.kpiLabel}>Kalan</div>
            <div style={s.kpiValue('#e65100')}>{kalanTutar.toLocaleString('tr-TR')} TL</div>
          </div>
        </div>

        {/* Kalemler tablosu */}
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Tedarikçi</th>
              <th style={s.th}>Tutar</th>
              <th style={s.th}>Not</th>
              <th style={s.th}>Durum</th>
              <th style={s.th}>Ödeme Yontemi</th>
              <th style={s.th}>Ödeme Tarihi</th>
              {canManage && p.durum === 'GONDERILDI' && <th style={s.th}>İşlem</th>}
            </tr>
          </thead>
          <tbody>
            {(p.kalemler || []).map(k => (
              <tr key={k.id} style={{ background: k.durum === 'ODENDI' ? '#f1f8e9' : '#fff' }}>
                <td style={{ ...s.td, fontWeight: '600' }}>{k.tedarikci_unvan}</td>
                <td style={{ ...s.td, fontWeight: '600' }}>{k.tutar.toLocaleString('tr-TR')} TL</td>
                <td style={s.td}>{k.not_kalem || '-'}</td>
                <td style={s.td}><span style={s.badge(k.durum)}>{k.durum}</span></td>
                <td style={s.td}>{k.odeme_yontemi || '-'}</td>
                <td style={s.td}>{k.odenen_tarih || '-'}</td>
                {canManage && p.durum === 'GONDERILDI' && (
                  <td style={s.td}>
                    {k.durum === 'BEKLIYOR' ? (
                      <button onClick={() => { setPayModal({ kalemId: k.id, tedarikçiUnvan: k.tedarikci_unvan, tutar: k.tutar }); setPayMethod('HAVALE'); }}
                        style={s.btnSm('#e8f5e9', '#2e7d32')}>Odedim</button>
                    ) : <span style={{ color: colors.textTertiary, fontSize: '12px' }}>Odendi</span>}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {p.notlar && (
          <div style={{ marginTop: '16px', background: colors.backgroundAlt, padding: '12px 16px', borderRadius: radius.md, fontSize: '14px', color: '#555' }}>
            <strong>Plan Notu:</strong> {p.notlar}
          </div>
        )}

        {/* Ödeme Modal */}
        {payModal && (
          <div style={s.modal} onClick={() => setPayModal(null)}>
            <div style={s.modalContent} onClick={e => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 16px 0' }}>Ödeme Onayi</h3>
              <p style={{ fontSize: '14px', color: '#555', marginBottom: '16px' }}>
                <strong>{payModal.tedarikçiUnvan}</strong> — {payModal.tutar.toLocaleString('tr-TR')} TL
              </p>
              <div style={s.formGroup}>
                <label style={s.label}>Ödeme Yontemi</label>
                <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={s.input}>
                  <option value="HAVALE">Havale / EFT</option>
                  <option value="NAKIT">Nakit</option>
                  <option value="KREDI_KARTI">Kredi Karti</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button onClick={() => setPayModal(null)} style={s.btn('#f0f0f0', '#555')}>İptal</button>
                <button onClick={handlePayKalem} style={s.btn('#2e7d32', '#fff')}>Odedim</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ============ LİSTE GÖRÜNÜMÜ ============
  const toplamTutar = filteredPlanlar.reduce((s2, p) => s2 + (p.toplam_tutar || 0), 0);
  const toplamOdenen = filteredPlanlar.reduce((s2, p) => s2 + (p.odenen_tutar || 0), 0);

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.title}>Ödeme Planlari</h1>
        {canCreate && <button onClick={() => setView('create')} style={s.btn('#1a1a2e', '#fff')}>+ Yeni Plan</button>}
      </div>

      {/* KPI */}
      <div style={s.kpiRow}>
        <div style={s.kpiCard('#1a1a2e')}>
          <div style={s.kpiLabel}>Toplam Plan</div>
          <div style={s.kpiValue('#1a1a2e')}>{planlar.length}</div>
        </div>
        <div style={s.kpiCard('#e65100')}>
          <div style={s.kpiLabel}>Gonderilmis</div>
          <div style={s.kpiValue('#e65100')}>{planlar.filter(p => p.durum === 'GONDERILDI').length}</div>
        </div>
        <div style={s.kpiCard('#2e7d32')}>
          <div style={s.kpiLabel}>Tamamlanan</div>
          <div style={s.kpiValue('#2e7d32')}>{planlar.filter(p => p.durum === 'TAMAMLANDI').length}</div>
        </div>
        <div style={s.kpiCard('#1565c0')}>
          <div style={s.kpiLabel}>Toplam Tutar</div>
          <div style={s.kpiValue('#1565c0')}>{toplamTutar.toLocaleString('tr-TR')} TL</div>
        </div>
        <div style={s.kpiCard('#00695c')}>
          <div style={s.kpiLabel}>Odenen</div>
          <div style={s.kpiValue('#00695c')}>{toplamOdenen.toLocaleString('tr-TR')} TL</div>
        </div>
      </div>

      {/* Filtreler */}
      <div style={s.filterRow}>
        {[
          { key: 'ALL', label: 'Tumu' },
          { key: 'TASLAK', label: 'Taslak' },
          { key: 'GONDERILDI', label: 'Gönderildi' },
          { key: 'TAMAMLANDI', label: 'Tamamlandı' }
        ].map(f => (
          <button key={f.key} style={s.filterBtn(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</button>
        ))}
      </div>

      {/* Plan kartları */}
      {filteredPlanlar.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: colors.textTertiary, fontSize: '15px' }}>
          {filter === 'ALL' ? 'Henuz ödeme planı olusturulmamis' : `${filter} durumunda plan yok`}
        </div>
      ) : filteredPlanlar.map(p => (
        <div key={p.id} style={s.card} onClick={() => loadPlanDetail(p.id)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a2e', marginBottom: '4px' }}>{p.plan_no}</div>
              <div style={{ fontSize: '13px', color: colors.textTertiary }}>{p.hafta_baslangic} — {p.hafta_bitis} | {p.oluşturan}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={s.badge(p.durum)}>{p.durum}</span>
              <div style={{ fontSize: '18px', fontWeight: '700', color: '#1a1a2e', marginTop: '4px' }}>
                {(p.toplam_tutar || 0).toLocaleString('tr-TR')} TL
              </div>
              {p.odenen_tutar > 0 && (
                <div style={{ fontSize: '12px', color: '#2e7d32' }}>
                  Odenen: {p.odenen_tutar.toLocaleString('tr-TR')} TL ({p.odenen_kalem}/{p.kalem_sayisi})
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default OdemePlaniPage;
